import { JSX, useEffect } from "react";
import { InputFieldComponent } from "./InputFieldComponent";
import CloseIcon from "../../../common/images/close.svg?react";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import {
    setNewEmailAddress,
    setConfirmEmailAddress,
    setEmailError,
} from "../EditEmployeeModalSlice";
import { validateEmailState } from "../utils/validateEmailState";

interface NewEmailAddressProps {
    onEmailChange: (email: string) => void;
    onErrorChange: (error: string | null) => void;
    initialEmailError: string | null;
    onClose: () => void;
}

export const NewEmailAddressComponent = ({
    onEmailChange,
    onErrorChange,
    initialEmailError,
    onClose,
}: NewEmailAddressProps): JSX.Element => {
    const dispatch = useAppDispatch();

    // Get email state from Redux
    const {
        newEmailAddress,
        confirmEmailAddress,
        emailError,
        newEmailError,
        confirmEmailError,
        newEmailSuccess,
        confirmEmailSuccess,
    } = useAppSelector((state) => state.editEmployeeModal);

    // Use selectors for derived state
    const getEmailsMatch = (newEmail: string, confirmEmail: string): boolean =>
        newEmail === confirmEmail && newEmail !== "";
    const getEmailsEmpty = (newEmail: string, confirmEmail: string): boolean =>
        newEmail === "" && confirmEmail === "";

    const emailsMatch = getEmailsMatch(newEmailAddress, confirmEmailAddress);
    const emailsEmpty = getEmailsEmpty(newEmailAddress, confirmEmailAddress);

    // Initialize email error from props if provided
    useEffect(() => {
        if (initialEmailError) {
            dispatch(setEmailError(initialEmailError));
        }
    }, [initialEmailError, dispatch]);

    // Update parent component when error changes
    useEffect(() => {
        onErrorChange(emailError);
    }, [emailError, onErrorChange]);

    // Update error and success states when emails change
    useEffect(() => {
        // Use the validateEmailState utility function to handle all validation logic
        validateEmailState(
            {
                newEmailAddress,
                confirmEmailAddress,
                emailError,
            },
            dispatch,
        );
    }, [newEmailAddress, confirmEmailAddress, emailError, dispatch]);

    // Update parent component when emails match
    useEffect(() => {
        if (emailsMatch) {
            onEmailChange(newEmailAddress);
        }
    }, [emailsMatch, newEmailAddress, onEmailChange]);

    return (
        <>
            <div className={"c-create-employee-modal__form__row"}>
                <div className={"c-new-email-address"}>
                    <div className={"c-new-email-address__fields"}>
                        <InputFieldComponent
                            id={4}
                            type={"email"}
                            required={true}
                            title={"Nieuw privé e-mailadres"}
                            errorMessage={
                                "Nieuw privé e-mailadres is verplicht"
                            }
                            onChange={(value: string) => {
                                dispatch(setNewEmailAddress(value));
                            }}
                            value={newEmailAddress}
                            isError={newEmailError}
                            placeholder={"Vul e-mailadres in"}
                            isSuccess={newEmailSuccess}
                        />
                        <InputFieldComponent
                            id={5}
                            type={"email"}
                            required={true}
                            title={"Herhaal nieuw privé e-mailadres"}
                            errorMessage={
                                "Herhaling nieuw e-mailadres is verplicht"
                            }
                            placeholder={"Vul e-mailadres in"}
                            onChange={(value: string) => {
                                dispatch(setConfirmEmailAddress(value));
                            }}
                            value={confirmEmailAddress}
                            isError={confirmEmailError}
                            isSuccess={confirmEmailSuccess}
                        />
                    </div>
                    <div className={"c-new-email-address__status"}>
                        <figure
                            className={`c-new-email-address__status__dot ${emailsEmpty ? "c-new-email-address__status__dot--empty" : emailsMatch ? "c-new-email-address__status__dot--complete" : "c-new-email-address__status__dot--mismatch"}`}
                        ></figure>
                        <p>
                            {emailsEmpty
                                ? "Vul het nieuwe e-mailadres in"
                                : emailsMatch
                                  ? "De e-mailadressen komen overeen"
                                  : "De e-mailadressen komen niet overeen"}
                        </p>
                    </div>
                    <button
                        type="button"
                        className={"c-new-email-address__close"}
                        aria-label="Sluiten"
                        onClick={() => {
                            onClose();
                        }}
                    >
                        <CloseIcon />
                    </button>
                </div>
            </div>
        </>
    );
};
